import { useNavigate } from 'react-router-dom'
import { useApplication } from '../hooks/useApplication'
import { useAuth } from '../hooks/useAuth'
import { useOrgan } from '../hooks/useOrgan'
import { usePermissions } from '../hooks/usePermissions'
import useBaseService from '../hooks/services/useBaseService'
import useQueryWithLoader from '../hooks/useQueryWithLoader'
import { entities } from '../constants/entities'
import ApplicationColumn from '../components/application/ApplicationColumn'
import BillingModuleCard from '../components/application/BillingModuleCard'
import { Box, Button, Container, Typography } from '@mui/material'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowLeft, faUserTie } from '@fortawesome/free-solid-svg-icons'
import { useEffect, useMemo } from 'react'

const ApplicationSelection = () => {
  const { selectApplication } = useApplication()
  const { selectOrgan } = useOrgan()
  const { user, logout } = useAuth()
  const { userRoles, UserHasRole } = usePermissions()
  const hasAdminRole = UserHasRole(['Super Admin', 'Admin'], userRoles)
  const isGestor = String(user?.role || '').trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR') === 'gestor'
  const navigate = useNavigate()
  const { get: baseServiceGet } = useBaseService(navigate)

  // 1. Busca todas as aplicações do usuário
  const { data: applications } = useQueryWithLoader({
    queryKey: ['unified-applications', user?.id],
    queryFn: async () => {
      const response = await baseServiceGet(
        entities.users.applications.get(user.id)
      )
      return response.result.data.filter((app) => app.active)
    },
    enabled: !!user,
  })

  // 1.1 🆕 Badges de pendências por módulo (apenas Gestor)
  const { data: pendingCounts } = useQueryWithLoader({
    queryKey: ['pending-counts', user?.id],
    queryFn: async () => {
      const response = await baseServiceGet(entities.dashboard.pendingCounts)
      return response.result
    },
    enabled: !!user && isGestor,
    showGlobalLoader: false,
  })

  const getPendingCount = useMemo(() => {
    if (!isGestor || !pendingCounts) return () => 0

    const normalize = (s) =>
      String(s || '')
        .toUpperCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')

    const countsByModule = {
      ALUGUEL: pendingCounts.pending_exchanges,
      MANUTENCAO: pendingCounts.pending_upgrades,
      ALMOXARIFADO: pendingCounts.zero_stock_products,
      FATURAMENTO: pendingCounts.pending_billings,
    }

    return (appName) => countsByModule[normalize(appName)] || 0
  }, [isGestor, pendingCounts])

  // 2. Separa aplicações normais do faturamento
  const { regularApplications, billingApplication } = useMemo(() => {
    const billing = applications?.find(
      (app) => app.name?.toUpperCase() === 'FATURAMENTO'
    )
    const regular = applications?.filter(
      (app) => app.name?.toUpperCase() !== 'FATURAMENTO'
    )
    return { regularApplications: regular, billingApplication: billing }
  }, [applications])

  // 3. 🆕 Auto-seleção baseada no dashboard_module do JWT
  const autoTargetAppName = useMemo(() => {
    const moduleToApp = {
      manutencao: 'MANUTENÇÃO',
      almoxarifado: 'ALMOXARIFADO',
    }

    // 1º tenta pelo claim do JWT
    if (user?.dashboardModule) {
      return moduleToApp[user.dashboardModule.toLowerCase()] || null
    }

    // 2º fallback pelo nome do cargo
    const role = (user?.role || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')

    if (role.includes('tecnico') || role.includes('manutencao'))
      return 'MANUTENÇÃO'
    if (role.includes('estoque') || role.includes('almoxarifado'))
      return 'ALMOXARIFADO'

    return null
  }, [user])

  // 4. 🆕 Busca as organizações do usuário APENAS da aplicação alvo (se houver)
  const { data: autoOrgans } = useQueryWithLoader({
    queryKey: ['auto-organ-select', user?.id, autoTargetAppName],
    queryFn: async () => {
      const normalize = (s) =>
        (s || '')
          .toUpperCase()
          .normalize('NFD')
          .replace(/[\u0300-\u036f]/g, '')

      const app = applications.find(
        (a) => normalize(a.name) === normalize(autoTargetAppName)
      )
      if (!app) return null

      const response = await baseServiceGet(
        entities.users.applicationsAndOrganizations(user.id, app.id)
      )
      return { app, organs: response.result.data }
    },
    enabled: !!user && !!autoTargetAppName && !!applications,
  })

  // 5. 🆕 Auto-redireciona pro dashboard quando os dados chegam
  useEffect(() => {
    if (!autoTargetAppName || !autoOrgans) return
    const { app, organs } = autoOrgans

    // Nada vinculado -> cai no fluxo manual (mostra a seleção normal)
    if (!organs || organs.length === 0) return

    // 🆕 Mais de uma organização -> deixa o usuário escolher, não força nada
    if (organs.length > 1) return

    // Exatamente uma organização -> não há escolha real a fazer, pula direto
    selectApplication(app)
    selectOrgan(organs[0])
    navigate('/dashboard', { replace: true })
  }, [autoOrgans, autoTargetAppName, selectApplication, selectOrgan, navigate])

  // 6. Função para lidar com a seleção manual de aplicação normal
  const handleSelect = (app, organ) => {
    selectApplication(app)
    selectOrgan(organ)
    navigate('/dashboard')
  }

  // 7. Função específica para faturamento (sem órgão)
  const handleBillingAccess = () => {
    if (billingApplication) {
      selectApplication(billingApplication)
      selectOrgan({
        id: 'faturamento',
        name: 'Faturamento',
        color: '#0d2142',
      })
      navigate(isGestor ? '/dashboard' : '/faturamento/faturar')
    }
  }

  // 8. 🆕 Enquanto carrega os dados da auto-seleção, não renderiza a tela de seleção
  if (
    autoTargetAppName &&
    (applications === undefined || autoOrgans === undefined)
  ) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(90deg, #0d2142 0%, #3478dd 100%)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Typography variant="h6" sx={{ color: 'white' }}>
          Carregando...
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(90deg, #0d2142 0%, #3478dd 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        position: 'relative',
      }}>
      <Button
        variant="contained"
        startIcon={<FontAwesomeIcon icon={faArrowLeft} />}
        onClick={() => logout()}
        sx={{
          position: 'absolute',
          top: 20,
          right: 20,
          width: 200,
          background: 'linear-gradient(90deg, #0d2142 0%, #3478dd 100%)',
          fontWeight: 'bold',
          '&:hover': { filter: 'brightness(1.2)' },
        }}>
        Sair
      </Button>

      {hasAdminRole && !isGestor && (
        <Button
          variant="contained"
          startIcon={<FontAwesomeIcon icon={faUserTie} />}
          onClick={() => {
            selectOrgan({
              id: 'admin',
              name: 'Admin',
              color: '#0d2142',
            })
            navigate('/dashboard')
          }}
          sx={{
            position: 'absolute',
            bottom: 60,
            right: 60,
            width: 90,
            height: 90,
            background: 'linear-gradient(90deg, #3e434b 0%, #b5c3d8 100%)',
            borderRadius: '50%',
            fontWeight: 'bold',
            '&:hover': { filter: 'brightness(1.2)' },
          }}>
          ADM
        </Button>
      )}

      <Container maxWidth="xl">
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            justifyContent: 'center',
            gap: 2,
          }}>
          {/* Renderiza aplicações normais */}
          {regularApplications?.map((app) => (
            <ApplicationColumn
              key={app.id}
              app={app}
              user={user}
              userRoles={userRoles}
              UserHasRole={UserHasRole}
              baseServiceGet={baseServiceGet}
              pendingCount={getPendingCount(app.name)}
              onSelect={(organ) => handleSelect(app, organ)}
            />
          ))}

          {/* Renderiza card de faturamento SE o usuário tiver permissão */}
          {billingApplication && (
            <BillingModuleCard
              app={billingApplication}
              pendingCount={getPendingCount(billingApplication.name)}
              onAccess={handleBillingAccess}
            />
          )}
        </Box>
      </Container>
    </Box>
  )
}

export default ApplicationSelection
